import {
  getAvailableShapes,
  getDefaultShape,
  getAllowedThicknesses,
  resolveThickness,
  thicknessToProperty,
  propertyToThickness,
  getModelFileBase,
  isLegLengthDiscrepancyAllowed,
  getAvailableSizes,
  pickAvailableSize,
} from "//sequencesoles.com/cdn/shop/t/13/assets/insole-shape-rules.js?v=44322936541969452771792486717";

class InsoleGenerator extends HTMLElement {
  constructor() {
    super();

    // Initialize properties
    this.fileUploadForm = null;
    this.customerDataForm = null;
    this.productForm = null;
    this.modelsData = null;

    this.fileUploadBtn = null;
    this.fileUploadInput = null;
    this.optionInputs = null;
    this.productInfoElements = null;

    this.customerLoaded = false;
    this.expertMode = false;
    
    // Store recommended models from APD file upload
    this.recommendedModels = null;
    
    // Track if expert user has manually edited models
    this.expertModelsEdited = false;

    // Products array to store data for both products
    this.models = [
      { side: "Left", product: null, variant: null, model: null },
      { side: "Right", product: null, variant: null, model: null },
    ];

    this.CustomError = class CustomError extends Error {
      constructor(code, message, errors) {
        super(message);
        this.code = code;
        this.errors = errors;
      }
    };

    this.amazonS3Url = "https://sequencetm.s3.eu-north-1.amazonaws.com";
    this.modelViewerRequests = new Map();

    this.CUSTOM_WIDTH_TOLERANCE = 4;
    this.CUSTOM_WIDTH_FALLBACK_MIN = 75;
    this.CUSTOM_WIDTH_FALLBACK_MAX = 115;

    this.SIZE_WIDTH_LOOKUP = {
      "35": {
        "sizeChart": {
          "Wide": 83.6,
          "Narrow": 79.8
        },
        "actualModelWidth": 91.77
      },
      "36": {
        "sizeChart": {
          "Wide": 85.2,
          "Narrow": 81.3
        },
        "actualModelWidth": 92.48
      },
      "37": {
        "sizeChart": {
          "Wide": 86.7,
          "Narrow": 82.7
        },
        "actualModelWidth": 93.45
      },
      "38": {
        "sizeChart": {
          "Wide": 88.2,
          "Narrow": 84.1
        },
        "actualModelWidth": 95.03
      },
      "39": {
        "sizeChart": {
          "Wide": 89.7,
          "Narrow": 85.6
        },
        "actualModelWidth": 96.13
      },
      "40": {
        "sizeChart": {
          "Wide": 91.3,
          "Narrow": 87.1
        },
        "actualModelWidth": 98.14
      },
      "41": {
        "sizeChart": {
          "Wide": 92.9,
          "Narrow": 88.7
        },
        "actualModelWidth": 98.85
      },
      "42": {
        "sizeChart": {
          "Wide": 94.4,
          "Narrow": 90.1
        },
        "actualModelWidth": 100.13
      },
      "43": {
        "sizeChart": {
          "Wide": 96.1,
          "Narrow": 91.7
        },
        "actualModelWidth": 101.57
      },
      "44": {
        "sizeChart": {
          "Wide": 97.6,
          "Narrow": 93.1
        },
        "actualModelWidth": 102.61
      },
      "45": {
        "sizeChart": {
          "Wide": 99.2,
          "Narrow": 94.7
        },
        "actualModelWidth": 104.61
      },
      "46": {
        "sizeChart": {
          "Wide": 100.7,
          "Narrow": 96.1
        },
        "actualModelWidth": 105.26
      },
      "47": {
        "sizeChart": {
          "Wide": 102.3,
          "Narrow": 97.6
        },
        "actualModelWidth": 106.61
      },
      "48": {
        "sizeChart": {
          "Wide": 103.7,
          "Narrow": 98.9
        },
        "actualModelWidth": 107.82
      },
      "49": {
        "sizeChart": {
          "Wide": 105.2,
          "Narrow": 100.3
        },
        "actualModelWidth": 108.87
      },
      "50": {
        "sizeChart": {
          "Wide": 106.6,
          "Narrow": 101.7
        },
        "actualModelWidth": 110.72
      }
    }     
    
    // Track if user has manually modified custom width inputs
    this.customWidthInputsDirty = false;
  }

  connectedCallback() {
    // Query and initialize DOM elements
    this.fileUploadForm = this.querySelector("[data-file-upload]");
    this.customerDataForm = this.querySelector("[data-customer-data]");
    this.productForm = this.querySelector("[data-product-form]");
    this.modelsData = JSON.parse(
      this.querySelector("[data-models-json]").textContent
    );
    this.translations = JSON.parse(
      this.querySelector("[data-translations-json]").textContent
    );
    this.materialsData = JSON.parse(
      this.querySelector("[data-materials-json]").textContent
    );
    this.expertMode = this.hasAttribute("data-expert-mode") && this.getAttribute("data-expert-mode") === "true";
    this.enableSizeRecommendation = this.hasAttribute("data-enable-size-recommendation") && this.getAttribute("data-enable-size-recommendation") === "true";
    this.currencyFormatter = new Intl.NumberFormat(Shopify.locale, {
      style: "currency",
      currency: Shopify.currency.active,
    });
    this.clearUploadButton = this.querySelector("[data-clear-upload]");
    this.optionInputs = this.productForm.querySelectorAll(
      "[data-product-option]"
    );
    this.lineProperties = this.productForm.querySelectorAll(
      "[data-line-property]"
    );
    this.fileUploadBtn = this.fileUploadForm.querySelector("button");
    this.fileUploadInput =
      this.fileUploadForm.querySelector('input[type="file"]');
    this.productInfoElements = this.querySelectorAll("[data-product-info]");
    this.infoToggleBtn = this.querySelector("[data-info-toggle]");
    this.infoElements = this.querySelectorAll("[data-info-snippet]");
    this.suggestionPopup = this.querySelector("#size-suggestion");
    this.suggestionApproveBtn = this.suggestionPopup?.querySelector(
      "[data-approve-suggestion]"
    );
    this.validationWarningPopup = this.querySelector("#validation-warnings");
    this.validationCancelBtn = this.validationWarningPopup.querySelector(
      "[data-cancel-validation]"
    );
    this.validationIgnoreBtn = this.validationWarningPopup.querySelector(
      "[data-ignore-validation]"
    );
    this.thicknessInputs = this.productForm.querySelectorAll("[data-thickness-input]");
    this.thicknessOptionElements = this.productForm.querySelectorAll("[data-thickness-option]");
    this.customWidthSwatch = this.querySelector("[data-custom-width-swatch]");
    this.customWidthOptionElements = this.productForm.querySelectorAll("[data-custom-width-option]");
    this.lldContainer = this.productForm.querySelector("[data-lld-container]");
    this.lldDisabledNotice = this.productForm.querySelector("[data-lld-disabled-notice]");
    this.rightSizeFieldset = this.productForm.querySelector('[data-size-side="Right"]');
    this.leftSizeLegend = this.productForm.querySelector('[data-size-side="Left"] [data-size-legend]');
    this.sizeChartTrigger = this.querySelector("[data-size-chart-trigger]");
    this.shapeInputs = this.querySelectorAll("[data-shape-input]");
    this.shapeDescription = this.querySelector("[data-shape-description]");
    this.flatApproved = this.getAttribute("data-flat-approved") === "true";
    const availableShapeKeys = (this.getAttribute("data-available-shapes") || "")
      .split(",")
      .filter(Boolean);
    this.shapes = getAvailableShapes(
      JSON.parse(this.querySelector("[data-shapes-json]").textContent),
      availableShapeKeys
    );
    this.defaultShape = getDefaultShape(this.shapes, this.getAttribute("data-default-shape"));
    this.lastWidthOption = "Wide";
    this.customWidthContainer = this.querySelector("[data-custom-width]");
    this.customWidthSelectors = this.customWidthContainer.querySelectorAll("[data-custom-width-input]");
    this.customWidthInputs = Array.from(this.customWidthSelectors).map(selector => selector.querySelector('input'));
    this.materialDropdownTrigger = this.querySelector("[data-material-dropdown-trigger]");
    this.materialPreviewGallery = this.querySelector("#material-preview-gallery");
    this.previewImagesContainer = this.querySelector("[data-preview-images]");
    this.skipUploadButton = this.querySelector("[data-skip-upload]");
    this.modelSelectorTriggers = this.querySelectorAll("[data-model-selector-trigger]");
    this.legAxisInputs = this.querySelectorAll("[data-leg-axis]");
    this.compensationContainer = this.querySelector("[data-compensation-container]");
    this.compensationInputs = this.querySelectorAll("[data-compensation]");
    this.legLengthInputs = this.productForm.querySelectorAll('input[name$="-LEG_LENGTH_DISCREPANCY"]');
    this.baseProductHandles = ["model-1-left", "model-1-right"];

    this.init();
  }

  async init() {
    await this.setInitialProductJson();
    this.addEventListeners();
    this.updateAllProductData();

    if (this.customWidthSwatch.checked) {
      this.setVariantInput("option2", "Wide");
    }

    // Initialize custom width defaults
    this.updateCustomWidthDefaults();
    // Reset dirty flag since we're setting initial defaults
    this.customWidthInputsDirty = false;

    this.applyShapeRules();
    this.observeGeneratorHeaderHeight();
    this.getURLParameters();
  }

  async getURLParameters() {
    const url = new URL(window.location.href);
    const proposalCode = url.searchParams.get("transactioncode");
    const reorderData = url.searchParams.get("reorder");
    const modelSpec = url.searchParams.get("models");

    if (!proposalCode && !reorderData && !modelSpec) return;

    try {
      // Handle transaction code (existing functionality)
      if (proposalCode) {
        // Initialize order link button functionality since transaction code exists
        this.initOrderLinkFeatures(proposalCode);
        
        const response = await fetch(`/apps/sequence-shop/proposal`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ proposal_code: proposalCode }),
        });

        if (response.status !== 200) {
          console.error("Request failed with status:", response.status);
          return;
        }

        const data = await response.json();

        const cartCheck = await this.checkCart();
        if (cartCheck === false) {
          return;
        }

        this.populateModelData(data.insoles, true);
        this.customerDataForm.querySelector('input[name="Customer name"]').value =
          data.patient.name;
        this.customerDataForm.querySelector(
          'input[name="Customer email"]'
        ).value = data.patient.email;

        this.toggleFormsVisibility(false);
        this.customerLoaded = true;
      }
      
      // Handle reorder data (new functionality)
      if (reorderData) {
        await this.handleReorder(reorderData);
      }

      // Handle model preselection from the Sequence reader
      if (modelSpec) {
        await this.handleModelPreselect(modelSpec);
      }
    } catch (error) {
      console.error("Error in getURLParameters:", error);
    }
  }

  // Preselects a model per foot from a "?models=<left>-<right>" parameter, where
  // each value is a custom.model_number. Used by the Sequence reader to hand over
  // its two chosen models. Expert mode only: the upload step is bypassed the same
  // way the Skip upload button does it, so a non-expert cannot skip it this way.
  async handleModelPreselect(spec) {
    if (!this.expertMode) {
      console.error("Model preselection requires expert mode");
      return;
    }

    const parts = String(spec).split("-");
    if (parts.length !== 2) {
      console.error("Invalid models parameter, expected <left>-<right>:", spec);
      return;
    }

    // Resolve model numbers to product handles before touching the cart, so a
    // bad parameter cannot clear a cart and then fail.
    const numbers = { Left: parts[0].trim(), Right: parts[1].trim() };
    const handles = {};
    for (const side of ["Left", "Right"]) {
      const match = this.modelsData.find(
        (m) =>
          m.metafields?.foot_side === side &&
          String(m.metafields?.model_number) === numbers[side]
      );
      if (!match) {
        console.error(`No ${side} product with model_number ${numbers[side]}`);
        return;
      }
      handles[side] = match.handle;
    }

    const cartCheck = await this.checkCart();
    if (cartCheck === false) {
      return;
    }

    // Same flag the manual model dropdown sets, so a later leg axis change knows
    // the models were chosen deliberately rather than derived from an upload.
    this.expertModelsEdited = true;
    for (const side of ["Left", "Right"]) {
      await this.selectModel(side, handles[side]);
    }

    this.toggleFormsVisibility(false);
    this.customerLoaded = true;
  }

  async handleReorder(encodedData) {
    try {
      // Decode base64 data (Unicode-safe decoding for international characters)
      const base64Decoded = atob(decodeURIComponent(encodedData));
      const decodedData = JSON.parse(decodeURIComponent(escape(base64Decoded)));
      
      // Check cart
      const cartCheck = await this.checkCart();
      if (cartCheck === false) {
        return;
      }

      // Fetch product JSONs and populate data for each item
      const items = decodedData.items || [];
      
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        if (!item.handle) {
          console.error('Missing product handle for item:', item);
          continue;
        }

        // Fetch product JSON
        const productJson = await this.fetchProductJson(item.handle);
        
        if (!productJson) {
          console.error('Failed to fetch product for handle:', item.handle);
          continue;
        }

        // Find the model index for this side
        const modelIndex = this.models.findIndex(m => m.side === item.side);
        if (modelIndex === -1) {
          console.error('Invalid side:', item.side);
          continue;
        }

        // Set the product
        this.models[modelIndex].product = productJson;

        // Find variant by ID
        const variant = productJson.variants.find(v => v.id === item.variantId);
        
        if (!variant) {
          console.error('Variant not found:', item.variantId);
          continue;
        }

        // Set variant options using the variant's option values
        // option1 = Size, option2 = Width, option3 = Base Thickness
        if (variant.option1) {
          const sizeInput = Array.from(this.optionInputs).find(
            input => input.name === `side:${item.side}-option1`
          );
          if (sizeInput) {
            sizeInput.value = variant.option1;
            sizeInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        if (variant.option2) {
          this.setOptionInput('option2', variant.option2, true, true);
        }
        // option3 is not restored: the thickness is a line property now and the
        // variant always uses the single visible option3 value.
      }

      // Set line properties
      if (items.length > 0) {
        const firstItem = items[0];

        // Shape first, because it decides which thickness / width / LLD options exist
        this.setShape(firstItem.shape);

        // Shore Hardness
        if (firstItem.shoreHardness) {
          const shoreInput = Array.from(this.lineProperties).find(
            input => input.name === 'SHORE_HARDNESS' && input.value === firstItem.shoreHardness
          );
          if (shoreInput) {
            shoreInput.checked = true;
            shoreInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        // Top Layer Material
        if (firstItem.topLayerMaterial) {
          const materialInput = Array.from(this.lineProperties).find(
            input => input.name === 'TOP_LAYER_MATERIAL' && input.value === firstItem.topLayerMaterial
          );
          if (materialInput) {
            materialInput.checked = true;
            materialInput.dispatchEvent(new Event('change', { bubbles: true }));
            
            // Also update the material dropdown display
            this.selectMaterial(firstItem.topLayerMaterial, materialInput.dataset.technicalValue);
          }
        }

        // Thickness (older orders only carry the translated "Low" flag)
        const thickness =
          propertyToThickness(firstItem.thickness) ||
          (firstItem.lowThickness ? "low" : "standard");
        this.setThickness(thickness);
      }

      // Set leg length discrepancy for each side
      for (const item of items) {
        if (item.legLengthDiscrepancy !== undefined && item.legLengthDiscrepancy !== null) {
          const legLengthInput = this.productForm.querySelector(
            `input[name="side:${item.side}-LEG_LENGTH_DISCREPANCY"]`
          );
          if (legLengthInput) {
            legLengthInput.value = item.legLengthDiscrepancy;
            legLengthInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      // Restore the compensation selection for the discrepancy
      const compensation = items.find((item) => item.compensation)?.compensation;
      if (compensation) {
        const compensationInput = Array.from(this.compensationInputs).find(
          (input) => input.value === compensation
        );
        if (compensationInput) {
          compensationInput.checked = true;
        }
      }
      this.updateCompensationVisibility();

      // Handle custom width
      const hasCustomWidth = items.some(item => item.customWidth);
      if (hasCustomWidth) {
        // Select custom width swatch
        if (this.customWidthSwatch) {
          this.customWidthSwatch.checked = true;
          this.customWidthSwatch.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Set custom width values for each side
        for (const item of items) {
          if (item.customWidth) {
            const customWidthInput = this.customWidthInputs.find(
              input => input.dataset.side === item.side
            );
            if (customWidthInput) {
              customWidthInput.value = item.customWidth;
              customWidthInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        }
        
        // Mark as dirty since we're loading saved custom values
        this.customWidthInputsDirty = true;
      }

      // Fetch and reconstruct APD files
      await this.fetchReorderFiles(items);

      // Populate customer data
      if (decodedData.customerName) {
        this.customerDataForm.querySelector('input[name="Customer name"]').value = decodedData.customerName;
      }
      if (decodedData.customerEmail) {
        this.customerDataForm.querySelector('input[name="Customer email"]').value = decodedData.customerEmail;
      }

      // Re-validate the restored selections against the shape (clamps LLD, hides
      // options the shape does not offer) and refresh variants, price and viewers
      this.applyShapeRules();

      // Skip file upload, show customer data form
      this.toggleFormsVisibility(false);
      this.customerLoaded = true;

    } catch (error) {
      console.error('Error handling reorder:', error);
      alert('There was an error loading your previous order. Please try again or contact support.');
    }
  }

  async fetchReorderFiles(items) {
    const files = [];
    
    for (const item of items) {
      if (!item.fileUrl) continue;

      try {
        // Fetch the file from the URL
        const response = await fetch(item.fileUrl);
        if (!response.ok) {
          console.error('Failed to fetch file:', item.fileUrl);
          continue;
        }

        const blob = await response.blob();
        
        // Extract filename from URL
        const urlParts = item.fileUrl.split('/');
        const filename = urlParts[urlParts.length - 1];
        
        // Create File object
        const file = new File([blob], filename, { type: 'application/octet-stream' });

        // Add to files array in the format expected by the generator
        files.push({
          side: item.side,
          originalFile: file,
          name: filename
        });

      } catch (error) {
        console.error('Error fetching file for', item.side, ':', error);
      }
    }

    // Set the files property
    if (files.length > 0) {
      this.files = files;
    }
  }
  
  initOrderLinkFeatures(transactionCode) {
    // Show the order link button
    const orderLinkButtonContainer = document.getElementById('order-link-button-container');
    if (orderLinkButtonContainer) {
      orderLinkButtonContainer.style.display = 'block';
    }
    
    // Set the full URL in the drawer
    const transactionCodeDisplay = document.getElementById('transaction-code-display');
    if (transactionCodeDisplay) {
      const currentUrl = window.location.href;
      transactionCodeDisplay.textContent = currentUrl;
    }
    
    // Generate QR code for current URL
    const qrCodeContainer = document.getElementById('qrcode-container');
    if (qrCodeContainer) {
      const currentUrl = window.location.href;
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(currentUrl)}`;
      
      const qrCodeImage = document.createElement('img');
      qrCodeImage.crossOrigin = 'anonymous'; // Allow cross-origin loading
      qrCodeImage.src = qrCodeUrl;
      qrCodeImage.alt = 'Order link QR code';
      qrCodeImage.style.margin = '0 auto';
      qrCodeImage.style.display = 'block';
      qrCodeContainer.appendChild(qrCodeImage);

    }
    
    // Add print functionality
    const printButton = document.getElementById('print-order-code');
    if (printButton) {
      printButton.addEventListener('click', function() {
        // Create a new window for printing
        const printWindow = window.open('', '_blank');
        
        if (!printWindow) {
          alert('Please allow pop-ups to print the order link');
          return;
        }
        
        // Get the content element
        const contentElement = document.getElementById('order-link-content');
        
        // Set up the print window using DOM methods
        const printDoc = printWindow.document;
        
        // Add HTML5 doctype
        printDoc.open();
        printDoc.close();
        
        // Create HTML structure
        const html = printDoc.createElement('html');
        const head = printDoc.createElement('head');
        const body = printDoc.createElement('body');
        
        html.appendChild(head);
        html.appendChild(body);
        
        // Add <title>
        const titleElement = printDoc.createElement('title');
        titleElement.textContent = 'Order Link';
        head.appendChild(titleElement);
        
        // Add <style>
        const styleElement = printDoc.createElement('style');
        styleElement.textContent = `
          body { 
            font-family: system-ui, sans-serif; 
            text-align: center; 
            padding: 20px; 
          }
          
          /* Make print-only elements visible */
          .print-only, .print-only-logo {
            display: block; 
          }
          
          /* Hide print button */
          .print-hide {
            display: none;
          }
          
          /* Basic styling */
          .print-only-logo img { 
            max-width: 200px; 
            margin-bottom: 1.5rem;
          }
          
          .order-link-description {
            margin-bottom: 1.5rem; 
          }
          
          .qrcode-container img {
            max-width: 200px;
            margin: 0 auto;
            display: block;
          }
          
          .transaction-code-container {
            background-color: #f7f7f7;
            border-radius: 5px;
            padding: 10px;
            display: inline-block;
            margin-top: 20px;
          }
          
          /* Page layout for print */
          @media print {
            @page { margin: 0.5cm; }
            body { padding: 0; }
          }
        `;
        head.appendChild(styleElement);
        
        // Add content
        const container = printDoc.createElement('div');
        container.className = 'print-content';
        container.innerHTML = contentElement.innerHTML; // Use innerHTML to preserve content
        body.appendChild(container);
        
        // Append the HTML structure to the document
        printDoc.documentElement.replaceWith(html);

        setTimeout(() => {
          printWindow.print();
          setTimeout(() => printWindow.close(), 1000);
        }, 500);
      });
    }
  }

  async setInitialProductJson() {
    try {
      const [leftJson, rightJson] = await Promise.all([
        this.fetchProductJson("model-1-left"),
        this.fetchProductJson("model-1-right"),
      ]);

      this.models[0].product = leftJson;
      this.models[1].product = rightJson;
    } catch (error) {
      this.handleError(error);
    }
  }

  addEventListeners() {
    this.fileUploadBtn.addEventListener("click", async () => {
      // Check if cart is empty before uploading new files
      const cartCheck = await this.checkCart();
      if (cartCheck === false) {
        return;
      }
      this.fileUploadInput.click();
    });
    this.fileUploadInput.addEventListener("change", (e) =>
      this.handleFileUpload(e)
    );
    this.optionInputs.forEach((input) => {
      input.addEventListener("click", () =>
        this.setOptionInput(input.name, input.value)
      );
      input.addEventListener("change", () =>
        this.setOptionInput(input.name, input.value)
      );
    });
    this.productForm.addEventListener("submit", (e) => {
      e.preventDefault();
      if (!this.isCustomerDataValid()) {
        this.toggleSubmitButtons(false);
        return;
      }
      this.addToCart();
    });
    this.clearUploadButton.addEventListener("click", () => this.clearUpload());
    this.infoToggleBtn.addEventListener("click", () => {
      this.infoElements.forEach((element) => {
        element.classList.toggle("generator__info-snippet--visible");
      });
    });
    this.suggestionApproveBtn?.addEventListener("click", () =>
      this.approveSuggestion()
    );

    this.shapeInputs.forEach((input) => {
      input.addEventListener("change", () => this.applyShapeRules());
    });

    // Leg length discrepancy availability depends on the thickness
    this.thicknessInputs.forEach((input) => {
      input.addEventListener("change", () => this.applyShapeRules());
    });

    this.customWidthSwatch.addEventListener("change", () => {
      this.setOptionInput("option2", "Wide", false, false); // Don't update width defaults
      this.customWidthSwatch.checked = true;
    });

    // Add click listener specifically for when user manually clicks Custom swatch
    this.customWidthSwatch.addEventListener("click", () => {
      // If inputs are not dirty (user hasn't manually input values), set to Wide defaults
      if (!this.customWidthInputsDirty) {
        this.customWidthInputs.forEach(input => {
          const side = input.dataset.side;
          const sizeInput = Array.from(this.optionInputs).find(
            i => i.name === `side:${side}-option1`
          );
          
          if (sizeInput && sizeInput.value) {
            const wideDefault = this.getWidthForSize(sizeInput.value, true);
            input.value = wideDefault;
          }
        });
        // Don't set dirty flag since this is programmatic
      }
      // If dirty, keep current values (user has manually input custom values)
    });

    // Add event listeners for both custom width inputs
    this.customWidthInputs.forEach((input, index) => {
      const selector = this.customWidthSelectors[index];
      
      const handleCustomWidthChange = () => {
        // Mark as dirty when user manually changes values
        this.customWidthInputsDirty = true;
        
        // Get the wide default for this size to calculate min/max
        const side = input.dataset.side;
        const sizeInput = Array.from(this.optionInputs).find(
          i => i.name === `side:${side}-option1`
        );
        
        let minValue = this.CUSTOM_WIDTH_FALLBACK_MIN;
        let maxValue = this.CUSTOM_WIDTH_FALLBACK_MAX;
        
        if (sizeInput && sizeInput.value) {
          const wideDefault = this.getWidthForSize(sizeInput.value, true);
          minValue = wideDefault - this.CUSTOM_WIDTH_TOLERANCE;
          maxValue = wideDefault + this.CUSTOM_WIDTH_TOLERANCE;
        }
        
        // Store original value before validation
        const originalValue = parseInt(input.value);
        
        // Apply min/max validation based on wide default +/- tolerance
        const validatedValue = Math.min(Math.max(input.value, minValue), maxValue);
        input.value = validatedValue;
        
        // Show validation notice if value was clamped
        const validationNotice = document.querySelector(`[data-validation-notice="${side}"]`);
        if (validationNotice) {
          if (originalValue < minValue) {
            // Show minimum reached message
            validationNotice.querySelector('small').textContent = `Minimum width reached (-7mm from default)`;
            validationNotice.style.display = 'block';
          } else if (originalValue > maxValue) {
            // Show maximum reached message
            validationNotice.querySelector('small').textContent = `Maximum width reached (+7mm from default)`;
            validationNotice.style.display = 'block';
          } else {
            // Hide validation notice if value is within range
            validationNotice.style.display = 'none';
          }
        }
        
        if (sizeInput && sizeInput.value) {
          const currentWidthOption = Array.from(this.optionInputs).find(
            i => i.name === "option2" && i.checked
          );
          const isWide = currentWidthOption ? currentWidthOption.value === "Wide" : true;
          const defaultWidth = this.getWidthForSize(sizeInput.value, isWide);
          
          // Switch to Custom if value differs from default
          if (parseInt(input.value) !== defaultWidth) {
            if (!this.customWidthSwatch.checked) {
              this.customWidthSwatch.checked = true;
              this.customWidthSwatch.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        }
      };
      
      // Listen for changes from manual input
      input.addEventListener("change", handleCustomWidthChange);
      input.addEventListener("blur", handleCustomWidthChange);
      
      // Listen for clicks on quantity-selector buttons
      const buttons = selector.querySelectorAll('.quantity-selector__button');
      buttons.forEach(button => {
        button.addEventListener('click', () => {
          // Small delay to let the quantity-input update the value
          setTimeout(handleCustomWidthChange, 10);
        });
      });
    });
    
    // Add event listeners for size changes to update width defaults
    this.optionInputs.forEach((input) => {
      if (input.name.includes("option1")) {
        input.addEventListener("change", () => {
          // The variant refresh from setOptionInput ran before this listener,
          // so a mirrored right size needs its own refresh
          if (this.mirrorSizeToRight(input)) {
            this.updateAllProductData();
          }
          this.updateCustomWidthDefaults();
          // Reset dirty flag since we programmatically updated defaults for new size
          this.customWidthInputsDirty = false;
        });
      }
    });

    // A leg length discrepancy toggles the compensation choice and selects the elevated preview model
    this.legLengthInputs.forEach((input) => {
      input.addEventListener("change", () => this.handleLegLengthChange());
      input.addEventListener("blur", () => this.handleLegLengthChange());

      const buttons = input.closest("quantity-selector")?.querySelectorAll(".quantity-selector__button") || [];
      buttons.forEach((button) => {
        button.addEventListener("click", () => {
          // Small delay to let the quantity-input update the value
          setTimeout(() => this.handleLegLengthChange(), 10);
        });
      });
    });

    this.compensationInputs.forEach((input) => {
      input.addEventListener("change", () => this.updateProductInfo());
    });

    // Material dropdown event listeners
    this.querySelectorAll('.material-option-main').forEach((option) => {
      option.addEventListener('click', (e) => {
        const materialValue = e.currentTarget.dataset.materialValue;
        const technicalValue = e.currentTarget.dataset.technicalValue;
        this.selectMaterial(materialValue, technicalValue);
      });
    });

    // View samples event listeners
    this.querySelectorAll('[data-view-samples]').forEach((button) => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        const materialName = e.currentTarget.dataset.viewSamples;
        this.openPreviewGallery(materialName);
      });
    });

    // Expert mode: Skip upload button
    if (this.skipUploadButton) {
      this.skipUploadButton.addEventListener('click', async () => {
        const cartCheck = await this.checkCart();
        if (cartCheck === false) {
          return;
        }
        this.toggleFormsVisibility(false);
        this.customerLoaded = true;
      });
    }

      // Expert mode: Model selector dropdowns
      if (this.expertMode && this.modelSelectorTriggers.length > 0) {
        // Sort listbox options by model number
        this.modelSelectorTriggers.forEach((trigger) => {
          const popover = trigger.nextElementSibling;
          if (popover && popover.tagName === 'X-POPOVER') {
            const listbox = popover.querySelector('x-listbox');
            if (listbox) {
              const options = Array.from(listbox.querySelectorAll('[data-model-handle]'));
              // Sort by model number
              options.sort((a, b) => {
                const modelNumA = a.dataset.modelNumber || '';
                const modelNumB = b.dataset.modelNumber || '';
                // Compare as numbers if both are numeric, otherwise as strings
                const numA = parseFloat(modelNumA);
                const numB = parseFloat(modelNumB);
                if (!isNaN(numA) && !isNaN(numB)) {
                  return numA - numB;
                }
                return modelNumA.localeCompare(modelNumB);
              });
              // Reorder DOM elements
              options.forEach(option => listbox.appendChild(option));
            }
          }
        });

      // Add click listeners
      this.querySelectorAll('[data-model-handle]').forEach((option) => {
        option.addEventListener('click', (e) => {
          e.stopPropagation();
          const modelHandle = e.currentTarget.dataset.modelHandle;
          const side = e.currentTarget.dataset.modelSide;
          if (modelHandle && side) {
            // Mark that expert has manually edited models
            this.expertModelsEdited = true;
            this.selectModel(side, modelHandle);
            // Close the popover
            const popover = e.currentTarget.closest('x-popover');
            if (popover && popover.hide) {
              popover.hide();
            }
          }
        });
      });
    }

    // Leg axis selector event listeners
    if (this.legAxisInputs.length > 0) {
      this.legAxisInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked) {
            this.selectModelsByLegAxis(input.value);
          }
        });
      });
    }
  }

  setOptionInput(optionName, optionValue, borders = true, updateWidthDefaults = true) {
    const optionSelect = Array.from(this.optionInputs).find(
      (input) => input.name === optionName && input.value === optionValue
    );
    if (!optionSelect) {
      console.warn(`No ${optionName} input with value ${optionValue}`);
      return;
    }
    const optionLabel = document.querySelector(
      `label[for="${optionSelect.id}"]`
    );

    const optionsWrapper = document.querySelector(
      `.variant-picker__option[data-option="${optionName}"]`
    );
    optionsWrapper
      ?.querySelectorAll(".generator-no-border")
      .forEach((element) => element.classList.remove("generator-no-border"));

    this.optionInputs.forEach((input) => {
      if (input.name !== optionName) return;
      input.checked = false;
    });

    if (!borders) optionLabel?.classList.add("generator-no-border");

    if (optionName === "option2") {
      // Only uncheck Custom if we're selecting Wide or Narrow (not Custom itself)
      if (optionValue !== "Custom") {
        this.customWidthSwatch.checked = false;
      }
      // Remembered so a shape without custom width can fall back to the last real width
      if (borders && (optionValue === "Wide" || optionValue === "Narrow")) {
        this.lastWidthOption = optionValue;
      }
    }

    optionSelect.checked = true;
    this.updateAllProductData();
    
    // Update custom width defaults only if requested
    if (optionName === "option2" && updateWidthDefaults) {
      this.updateCustomWidthDefaults();
      // Reset dirty flag since we programmatically set defaults
      this.customWidthInputsDirty = false;
    }
  }

  async clearUpload() {
    this.fileUploadInput.value = "";
    this.customerDataForm.reset();
    this.clearCartAttributes();
    this.productForm.reset();
    this.toggleFormsVisibility(true);
    await this.setInitialProductJson();
    this.updateAllProductData();
    this.setShape(this.defaultShape?.key);
    this.files = null;
    this.recommendedModels = null; // Clear recommended models when clearing upload
    this.expertModelsEdited = false; // Reset expert models edited flag
    // Reset dirty flag when clearing upload
    this.customWidthInputsDirty = false;
  }

  isCustomerDataValid() {
    const requiredInputs =
      this.customerDataForm.querySelectorAll("input[required]");
    const isFormValid = Array.from(requiredInputs).every(
      (input) => input.value
    );
    if (this.customerLoaded && !isFormValid) {
      this.customerDataForm.reportValidity();
      return false;
    }
    return true;
  }

  async checkCart() {
    //Make sure cart is empty before uploading new files. If not empty, prompt user to clear cart.
    const cart = await this.getCart();
    if (cart.item_count) {
      const confirmClear = confirm(this.translations.clearCartPrompt);
      if (confirmClear) {
        await this.clearCart();
        await this.clearCartAttributes();
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }

  async getCart() {
    try {
      const response = await fetch(window.Shopify.routes.root + "cart.js");
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return await response.json();
    } catch (error) {
      this.handleError(error);
    }
  }

  async clearCart() {
    try {
      const response = await fetch(
        window.Shopify.routes.root + "cart/clear.js",
        {
          method: "POST",
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  async clearCartAttributes() {
    try {
      const response = await fetch(
        window.Shopify.routes.root + "cart/update.js",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ attributes: {} }),
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  async addToCart() {
    this.toggleSubmitButtons(true);

    const data = this.prepareCartData();
    console.log(data);
    
    const formData = new FormData();

    // Add cart items data
    // Serialize items data into FormData for each item
    data.items.forEach((item, index) => {
      formData.append(`items[${index}][id]`, item.id);
      formData.append(`items[${index}][quantity]`, item.quantity);

      Object.keys(item.properties).forEach((key) => {
        formData.append(
          `items[${index}][properties][${key}]`,
          item.properties[key]
        );
      });
      // Append file inputs using the uploaded files data
      if (!this.files) return;
      const file = this.files.find((f) => f.side === item.properties._SIDE);
      if (file) {
        formData.append(
          `items[${index}][properties][_file]`,
          file.originalFile
        );
      }
    });

    try {
      await this.clearCartAttributes();

      const addResponse = await fetch(
        window.Shopify.routes.root + "cart/add.js",
        {
          method: "POST",
          // headers: { 'Content-Type': 'application/json' },
          body: formData,
        }
      );

      if (addResponse.ok) {
        const customerData = new FormData(this.customerDataForm);
        const attributes = Object.fromEntries(customerData);
        
        // Add expert models edited attribute if applicable
        if (this.expertMode && this.expertModelsEdited) {
          attributes['expert_mode_edited'] = 'true';
        }
        
        await fetch(window.Shopify.routes.root + "cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ attributes }),
        });

        window.location.href = window.Shopify.routes.root + "cart";
      } else {
        console.error("Error adding items to cart:", addResponse.statusText);
      }
    } catch (error) {
      this.handleError(error);
    } finally {
      this.toggleSubmitButtons(false);
    }
  }

  prepareCartData() {
    const selectedLineProperties = Array.from(this.lineProperties).filter(
      (input) => {
        if (input.tagName === "INPUT") {
          return (
            (input.type !== "radio" && input.type !== "checkbox") ||
            input.checked
          );
        }
        return false;
      }
    );

    const mapProperties = (filterFn) =>
      selectedLineProperties.filter(filterFn).map((input) => {
        // Get the technical value if available, otherwise use the regular value
        const value = input.dataset.technicalValue || input.value;
        return {
          [input.name.split("-")[1] || input.name]: value,
        };
      });

    const commonLineProperties = mapProperties(
      (input) => !input.name.includes(":")
    );
    //For each commonLineProperty, add _ to the beginning of the key, and then add the translated key also
    commonLineProperties.forEach((property) => {
      const key = Object.keys(property)[0];
      const technicalValue = property[key];
      
      // Find the original input element to get display value
      const inputElement = selectedLineProperties.find(input => 
        (input.name.split("-")[1] || input.name) === key && input.checked
      );
      
      // For display, use the actual input value (translated) instead of technical value
      const displayValue = inputElement && inputElement.dataset.technicalValue ? 
        inputElement.value : technicalValue;
      
      property[`_${key}`] = technicalValue;
      property[this.translations.line_properties[key]] = displayValue;
      delete property[key];
    });

    const shape = this.getShapeConfig();
    const thickness = this.getSelectedThickness();
    const thicknessProperty = thicknessToProperty(thickness);
    const thicknessLabel =
      this.translations.variant_option?.BASE_THICKNESS?.[thicknessProperty.toUpperCase()] ||
      thicknessProperty;

    return {
      items: this.models.map((model) => {
        const lineProperties = mapProperties((input) =>
          input.name.includes(`side:${model.side}`)
        );
        lineProperties.forEach((property) => {
          const key = Object.keys(property)[0];
          property[`_${key}`] = property[key];
          if (key === "LEG_LENGTH_DISCREPANCY") {
            let displayValue = `${property[key]}mm`;
            if (parseInt(property[key]) >= 1) {
              const compensation = this.getSelectedCompensation();
              const compensationLabel =
                this.translations.compensation_options?.[compensation] || compensation;
              displayValue += ` (${compensationLabel})`;
              property._COMPENSATION = compensation;
            }
            property[this.translations.line_properties[key]] = displayValue;
          } else {
            property[this.translations.line_properties[key]] = property[key];
          }
          delete property[key];
        });
        return {
          id: model.variant.id,
          quantity: 1,
          properties: {
            _SIDE: model.side,
            [this.translations.SIDE]:
              this.translations[model.side.toUpperCase()],
            ...Object.assign({}, ...commonLineProperties, ...lineProperties),
            ...(this.customWidthSwatch.checked && this.customWidthInputs.length
              ? (() => {
                  const sideInput = this.customWidthInputs.find(input => input.dataset.side === model.side);
                  if (sideInput) {
                    const customWidth = parseInt(sideInput.value);
                    const sizeInput = Array.from(this.optionInputs).find(
                      i => i.name === `side:${model.side}-option1`
                    );
                    const size = sizeInput ? sizeInput.value : null;
                    
                    const printingWidth = this.calculatePrintingWidth(customWidth, size);
                    // _CUSTOM_WIDTH and _PRINTING_WIDTH are locale-independent technical values
                    // consumed by the Houdini CSV export and must not be built from translations
                    return {
                      _CUSTOM_WIDTH: `${customWidth}`,
                      [this.translations.line_properties.CUSTOM_WIDTH]: `${customWidth}mm`,
                      _PRINTING_WIDTH: `${printingWidth}`,
                      [`_${this.translations.line_properties.PRINTING_WIDTH}`]: `${printingWidth}mm`
                    };
                  }
                  return null;
                })()
              : null),
            _BASE_THICKNESS: thicknessProperty,
            [this.translations.line_properties.BASE_THICKNESS]: thicknessLabel,
            // _SHAPE and _PRINTING_FILE are technical values for the operator / packing slip
            _SHAPE: shape?.key || "sequence",
            [this.translations.line_properties.SHAPE]: shape?.name || "Sequence",
            _PRINTING_FILE: `${getModelFileBase(
              model.variant.sku,
              shape,
              thickness,
              this.getLegLengthDiscrepancy(model.side),
              this.getSelectedCompensation()
            )}.stl`,
          },
        };
      }),
    };
  }

  getSelectedCompensation() {
    const checkedInput = Array.from(this.compensationInputs).find((input) => input.checked);
    return checkedInput ? checkedInput.value : "Heel wedge";
  }

  getLegLengthDiscrepancy(side) {
    const input = Array.from(this.legLengthInputs).find(
      (candidate) => candidate.name === `side:${side}-LEG_LENGTH_DISCREPANCY`
    );
    return input ? parseInt(input.value) || 0 : 0;
  }

  handleLegLengthChange() {
    this.updateCompensationVisibility();
    this.updateProductInfo();
  }

  updateCompensationVisibility() {
    const hasDiscrepancy = Array.from(this.legLengthInputs).some(
      (input) => parseInt(input.value) >= 1
    );

    if (this.compensationContainer) {
      this.compensationContainer.style.display = hasDiscrepancy ? "" : "none";
    }

    // Reset to the default option when hidden so a stale selection can't linger
    if (!hasDiscrepancy) {
      this.compensationInputs.forEach((input) => {
        input.checked = input.value === "Heel wedge";
      });
    }
  }

  toggleSubmitButtons(disable) {
    const formId = this.productForm.getAttribute("id");
    const submitButtons = document.querySelectorAll(
      `button[type="submit"][form="${formId}"]`
    );
    submitButtons.forEach((submitButton) => {
      if (disable) {
        submitButton.setAttribute("disabled", "disabled");
        submitButton.setAttribute("aria-busy", "true");
      } else {
        submitButton.removeAttribute("disabled");
        submitButton.removeAttribute("aria-busy");
      }
    });
  }

  isValidFileType(file) {
    const validExtensions = [".apd"];
    const fileExtension = file.name.split(".").pop().toLowerCase();
    return validExtensions.includes(`.${fileExtension}`);
  }

  handleFileUpload(e) {
    const { files } = e.target;
    this.clearErrorBanner();

    if (files.length !== 2) {
      this.handleError(
        new this.CustomError("INVALID_FILE_COUNT", "Please upload 2 files", []),
        true
      );
      e.target.value = "";
    } else if (Array.from(files).some((f) => !this.isValidFileType(f))) {
      this.handleError(
        new this.CustomError(
          "INVALID_FILE_TYPE",
          "Only .apd files accepted",
          []
        ),
        true
      );
      e.target.value = "";
    } else {
      this.setLoading(true);
      this.uploadFiles(files)
        .then(() => {})
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.setLoading(false);
          e.target.value = "";
        });
    }
  }

  handleError(error, showErrorBanner = false) {
    if (error instanceof this.CustomError) {
      if (showErrorBanner) {
        this.showErrorBanner(error);
      }
    } else {
      console.error("Unexpected error:", error);
      if (showErrorBanner) {
        this.showErrorBanner(
          new this.CustomError(
            "INTERNAL_SERVER_ERROR",
            "There was an error processing your request",
            []
          )
        );
      }
    }
  }

  clearErrorBanner() {
    const errorBanner = this.querySelector(".banner--error");
    if (errorBanner) {
      errorBanner.remove();
    }
  }

  showErrorBanner(error) {
    let errors = "";

    if (error.errors && error.errors.length) {
      errors = error.errors
        .map((err) => {
          let message = this.translations[err.code] || err.message;
          if (err.code === "FIELD_MISMATCH") {
            message = message.replace(
              "{{ field }}",
              err.field
            );
          }
          return message;
        })
        .join("<br>");
    } else {
      errors = this.translations[error.code] || error.message;
    }

    const errorBanner = document.createElement("div");
    errorBanner.classList.add("banner", "banner--error", "justify-center");
    errorBanner.setAttribute("role", "alert");
    errorBanner.style.gridColumn = "1/-1";
    errorBanner.style.marginLeft = "2rem";
    errorBanner.innerHTML = `
      <svg role="presentation" focusable="false" width="18" height="18" class="offset-icon icon icon-error" style="--icon-height: 18px" viewBox="0 0 18 18">
        <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="currentColor"></path>
        <path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>
      </svg>
      <p>${errors}</p>
    `;
    this.fileUploadForm.after(errorBanner);
  }

  setLoading(isLoading) {
    const mainElement = this.querySelector(".generator__main");
    if (isLoading) {
      this.fileUploadBtn.setAttribute("aria-busy", "true");
      this.fileUploadBtn.setAttribute("disabled", "disabled");
      mainElement.style.opacity = 0.5;
      mainElement.style.pointerEvents = "none";
    } else {
      this.fileUploadBtn.removeAttribute("aria-busy");
      this.fileUploadBtn.removeAttribute("disabled");
      mainElement.style.opacity = 1;
      mainElement.style.pointerEvents = "all";
    }
  }

  updateAllProductData() {
    this.updateVariants();
    this.updatePrice();
    this.updateProductInfo();
  }
  
  getWidthForSize(size, isWide = true) {
    const sizeData = this.SIZE_WIDTH_LOOKUP[size.toString()];
    if (!sizeData) return 90; // Default fallback
    
    // Use the sizeChart values from the lookup table
    const widthValue = isWide ? sizeData.sizeChart.Wide : sizeData.sizeChart.Narrow;
    return Math.round(widthValue);
  }
  
  calculatePrintingWidth(customWidth, size) {
    const sizeData = this.SIZE_WIDTH_LOOKUP[size.toString()];
    if (!sizeData) return customWidth; // Fallback if size not found
    
    // Custom width is always based on Wide, so use Wide sizeChart value
    const sizeChartWidth = sizeData.sizeChart.Wide;
    
    // Calculate printing width = custom width + (actual model width - size chart width)
    const printingWidth = customWidth + (sizeData.actualModelWidth - sizeChartWidth);
    
    return Math.round(printingWidth * 10) / 10; // Round to 1 decimal place
  }
  
  hideValidationNotices() {
    // Hide all validation notices
    const notices = document.querySelectorAll('[data-validation-notice]');
    notices.forEach(notice => {
      notice.style.display = 'none';
    });
  }

  updateCustomWidthDefaults() {
    console.log("Updating custom width defaults...");
    console.log("Custom swatch checked:", this.customWidthSwatch.checked);
    
    // Always update defaults when size or width options change
    // This ensures values stay within valid range for the new size
    
    // Hide validation notices since we're setting new defaults
    this.hideValidationNotices();
    
    // Get current width option (Wide or Narrow)
    const currentWidthOption = Array.from(this.optionInputs).find(
      input => input.name === "option2" && input.checked
    );
    const isWide = currentWidthOption ? currentWidthOption.value === "Wide" : true;
    
    // Update each custom width input based on its corresponding size
    this.customWidthInputs.forEach(input => {
      const side = input.dataset.side;
      const sizeInput = Array.from(this.optionInputs).find(
        i => i.name === `side:${side}-option1`
      );
      
      if (sizeInput && sizeInput.value) {
        const defaultWidth = this.getWidthForSize(sizeInput.value, isWide);
        input.value = defaultWidth;
      }
    });
    
    // If Custom was selected, switch back to the appropriate width option (Wide/Narrow)
    // since we've reset the values to defaults
    if (this.customWidthSwatch.checked) {
      const currentWidthOption = Array.from(this.optionInputs).find(
        input => input.name === "option2" && input.checked
      );
      if (currentWidthOption && (currentWidthOption.value === "Wide" || currentWidthOption.value === "Narrow")) {
        // Custom swatch will be unchecked automatically when the width option is selected
        currentWidthOption.checked = true;
        currentWidthOption.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  updateVariants() {
    this.models.forEach((model) => {
      model.variant = this.getVariantFromOptions(model.product, model.side);
    });
  }

  updatePrice() {
    const totalPrice =
      this.models.reduce(
        (sum, model) => sum + (model.variant ? model.variant.price : 0),
        0
      ) / 100;
    this.querySelector("[data-total-price]").textContent =
      this.currencyFormatter.format(totalPrice);
  }

  updateProductInfo() {
    this.models.forEach((model) => {
      const productInfo = this.querySelector(
        `[data-product-info="${model.side}"]`
      );
      if (!productInfo) return;

      const titleElement = productInfo.querySelector("[data-product-title]");
      if (titleElement) {
        titleElement.textContent = model.product.title;
      }
      
      productInfo.querySelector("[data-product-sku]").textContent =
        model.variant.sku;
      productInfo.querySelector("[data-product-description]").innerHTML =
        model.product.description;

      const modelViewer = productInfo.querySelector("model-viewer");
      if (modelViewer) {
        this.updateModelViewer(modelViewer, model.variant, model.side);
      }
    });
  }

  async updateModelViewer(viewer, variant, side) {
    if (!variant || !variant.sku) return;

    const requestId = (this.modelViewerRequests.get(viewer) || 0) + 1;
    this.modelViewerRequests.set(viewer, requestId);

    const fileBase = getModelFileBase(
      variant.sku,
      this.getShapeConfig(),
      this.getSelectedThickness(),
      this.getLegLengthDiscrepancy(side),
      this.getSelectedCompensation()
    );
    const src = `${this.amazonS3Url}/${fileBase}.glb`;

    try {
      const response = await fetch(src);
      if (this.modelViewerRequests.get(viewer) !== requestId) return;
      if (response.ok) {
        viewer.setAttribute("src", src);
      } else {
        console.error(`Error: ${src} returned status ${response.status}`);
      }
    } catch (error) {
      console.error(`Error fetching ${src}:`, error);
    }
  }

  getVariantFromOptions(product, side) {
    const options = [];
    const selectedOptions = Array.from(this.optionInputs).filter((input) => {
      if (input.tagName === "SELECT") {
        return input.value;
      } else if (
        input.tagName === "INPUT" &&
        (input.type === "radio" || input.type === "checkbox")
      ) {
        return input.checked;
      }
    });

    selectedOptions.forEach((input) => {
      const { name, value } = input;
      if (name.includes(":")) {
        const [optionSide, optionName] = name.split("-");
        if (`side:${side}` === optionSide) {
          options.push({ name: optionName, value });
        }
      } else {
        options.push({ name, value });
      }
    });

    return (
      product.variants.find((variant) => {
        return options.every((option) => variant[option.name] === option.value);
      }) || null
    );
  }

  async uploadFiles(files) {
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append("files", file));

    try {
      const response = await fetch(`/apps/sequence-shop/upload`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        if (data.validationErrors && data.validationErrors.length) {
          await this.showValidationWarnings(data.validationErrors);
        }
        // Based on data.files, find the uloaded file with Left value and Right value. In files.parsed.Customer.FootSide
        this.files = data.files.map((file) => {
          return {
            ...file,
            originalFile: Array.from(files).find((f) => f.name === file.name),
          };
        });
        // Pass false to show size suggestion modal for new file uploads
        this.populateModelData(data.insoles, false);
        this.populateCustomerData(data.extractedValues);
        this.toggleFormsVisibility(false);
        this.customerLoaded = true;
      } else {
        throw new this.CustomError(data.code, data.message, data.errors);
      }
    } catch (error) {
      if (error === "Ignore") {
        return;
      }
      this.handleError(
        error instanceof this.CustomError
          ? error
          : new this.CustomError(
              "INTERNAL_SERVER_ERROR",
              "There was an error uploading the files",
              []
            ),
        true
      );
    }
  }

  populateCustomerData(extractedValues) {
    const name = [extractedValues.firstName, extractedValues.lastName]
      .filter(Boolean)
      .join(" ");
    if (name) {
      this.customerDataForm.querySelector('input[name="Customer name"]').value =
        name;
    }
  }

  showValidationWarnings(warnings) {
    return new Promise((resolve, reject) => {
      let text = "";
      if (warnings && warnings.length) {
        text = warnings
          .map((err) => {
            let message = this.translations[err.code] || err.message;
            if (err.code === "FIELD_MISMATCH") {
              message = message.replace(
                "{{ field }}",
                err.field
              );
            }
            return message;
          })
          .join("<br>");
      }

      this.validationWarningPopup.querySelector(
        "[data-validation-warnings]"
      ).innerHTML = text;
      this.validationWarningPopup.show();

      this.validationIgnoreBtn.addEventListener("click", () => {
        this.validationWarningPopup.hide();
        resolve();
      });
      this.validationCancelBtn.addEventListener("click", () => {
        this.clearUpload();
        this.validationWarningPopup.hide();
        reject("Ignore");
      });
    });
  }

  openSuggestionDrawer() {
    if (!this.enableSizeRecommendation || !this.suggestionPopup) return;

    const leftSizeDimension = this.models
      .find((m) => m.side === "Left")
      ?.model.dimensions.find((d) => d.dimension === "foot-size-eu");
    const rightSizeDimension = this.models
      .find((m) => m.side === "Right")
      ?.model.dimensions.find((d) => d.dimension === "foot-size-eu");

    if (
      leftSizeDimension &&
      leftSizeDimension.confidence >= 0.9 &&
      rightSizeDimension &&
      rightSizeDimension.confidence >= 0.9
    ) {
      this.suggestionPopup.querySelector("[data-size-left]").textContent =
        leftSizeDimension.value;
      this.suggestionPopup.querySelector("[data-size-right]").textContent =
        rightSizeDimension.value;
      this.suggestionPopup.show();
    }
  }

  approveSuggestion() {
    if (!this.enableSizeRecommendation || !this.suggestionPopup) return;

    this.models.forEach((model) => {
      const sizeDimension = model.model.dimensions.find(
        (d) => d.dimension === "foot-size-eu"
      );
      if (sizeDimension) {
        const sizeInput = Array.from(this.optionInputs).find(
          (input) => input.name === `side:${model.side}-option1`
        );
        const size = sizeDimension.value?.replace("W", "");
        const width = sizeDimension.value?.includes("W") ? "Wide" : "Narrow";
        const widthInput = Array.from(this.optionInputs).find(
          (input) => input.name === `option2` && input.value === width
        );
        sizeInput.value = size;
        widthInput.checked = true;
        const event = new Event("change", { bubbles: true });
        sizeInput.dispatchEvent(event);
        widthInput.dispatchEvent(event);
      }
    });
    this.suggestionPopup.hide();
    // Re-applies the shape rules so a single-size shape mirrors the left foot
    this.applyShapeRules();
  }

  async populateModelData(models, url = false) {
    const findModelProduct = (footSide, model) => {
      return this.modelsData.find(
        (m) =>
          m.metafields.model_number == model &&
          m.metafields.foot_side == footSide
      );
    };

    const leftModel = models.find((m) => m.foot === "left");
    const rightModel = models.find((m) => m.foot === "right");

    const leftProduct = findModelProduct("Left", leftModel.model);
    const rightProduct = findModelProduct("Right", rightModel.model);

    if (leftProduct && rightProduct) {
      try {
        const [leftJson, rightJson] = await Promise.all([
          this.fetchProductJson(leftProduct.handle),
          this.fetchProductJson(rightProduct.handle),
        ]);

        const leftModelIndex = this.models.findIndex((m) => m.side === "Left");
        const rightModelIndex = this.models.findIndex(
          (m) => m.side === "Right"
        );

        this.models[leftModelIndex].product = leftJson;
        this.models[leftModelIndex].model = leftModel;
        this.models[rightModelIndex].product = rightJson;
        this.models[rightModelIndex].model = rightModel;

        // Store recommended models from APD upload for later use (only for new uploads, not URL-based)
        if (!url) {
          this.recommendedModels = {
            left: { handle: leftProduct.handle, product: leftJson },
            right: { handle: rightProduct.handle, product: rightJson }
          };
        }

        this.updateAllProductData();
        if (!url) {
          if (leftModel.dimensions?.length && rightModel.dimensions?.length) {
            this.openSuggestionDrawer();
          }
        } else {
          this.models.forEach((model) => {
            const sizeDimension = model.model.dimensions.find(
              (d) => d.dimension === "foot-size-eu"
            );
            if (sizeDimension) {
              const sizeInput = Array.from(this.optionInputs).find(
                (input) => input.name === `side:${model.side}-option1`
              );
              const size = this.extractNumberAndLetters(sizeDimension.value);

              const width = size.letters?.includes("W") ? "Wide" : "Narrow";
              const widthInput = Array.from(this.optionInputs).find(
                (input) => input.name === `option2` && input.value === width
              );
              sizeInput.value = size.number;
              widthInput.checked = true;
              const event = new Event("change", { bubbles: true });
              sizeInput.dispatchEvent(event);
              widthInput.dispatchEvent(event);
            }
          });
          // Re-applies the shape rules so a single-size shape mirrors the left foot
          this.applyShapeRules();
        }
      } catch (error) {
        this.handleError(error);
      }
    }
  }

  extractNumberAndLetters(input) {
    const match = input.match(/^(\d+)([A-Za-z]*)$/);
    if (match) {
      return {
        number: parseInt(match[1], 10),
        letters: match[2] || null,
      };
    }
    return null;
  }

  async fetchProductJson(handle) {
    try {
      const response = await fetch(
        window.Shopify.routes.root + `products/${handle}.js`
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return await response.json();
    } catch (error) {
      this.handleError(error);
    }
  }

  async selectModel(side, modelHandle, skipLegAxisReset = false) {
    try {
      const productJson = await this.fetchProductJson(modelHandle);
      if (!productJson) {
        console.error('Failed to fetch product for handle:', modelHandle);
        return;
      }

      const modelIndex = this.models.findIndex(m => m.side === side);
      if (modelIndex === -1) {
        console.error('Invalid side:', side);
        return;
      }

      // Update the product
      this.models[modelIndex].product = productJson;
      // Set variant to null so updateVariants() can determine the correct variant
      this.models[modelIndex].variant = null;

      // Update all product data (variants, price, product info)
      // This will call updateVariants() which uses getVariantFromOptions() to find the correct variant
      this.updateAllProductData();

      // If in expert mode, reset leg axis selector to Parallel when models are manually changed
      // Skip this if the change is coming from leg axis selector itself
      if (this.expertMode && !skipLegAxisReset) {
        const parallelInput = this.querySelector('[data-leg-axis][value="Parallel"]');
        if (parallelInput) {
          parallelInput.checked = true;
          // Don't trigger change event to avoid resetting models
        }
      }
    } catch (error) {
      console.error('Error selecting model:', error);
      this.handleError(error);
    }
  }

  async selectModelsByLegAxis(legAxis) {
    try {
      const legAxisLower = legAxis.toLowerCase();
      if (legAxisLower === 'parallel') {
        // If we have recommended models from APD upload, use those; otherwise use default models
        if (this.recommendedModels) {
          this.models[0].product = this.recommendedModels.left.product;
          this.models[0].variant = null;
          this.models[1].product = this.recommendedModels.right.product;
          this.models[1].variant = null;
          this.updateAllProductData();
        } else {
          // Reset to default models using existing helper function
          await this.setInitialProductJson();
          this.updateAllProductData();
        }
      } else {
        // Find models with matching leg_axis for each side (case-insensitive)
        // Models without leg_axis metafield default to "parallel"
        const leftModel = this.modelsData.find(
          m => m.metafields?.foot_side === 'Left' && (m.metafields?.leg_axis || 'parallel').toLowerCase() === legAxisLower
        );
        const rightModel = this.modelsData.find(
          m => m.metafields?.foot_side === 'Right' && (m.metafields?.leg_axis || 'parallel').toLowerCase() === legAxisLower
        );

        if (leftModel && rightModel) {
          await Promise.all([
            this.selectModel('Left', leftModel.handle, true),
            this.selectModel('Right', rightModel.handle, true),
          ]);
        } else {
          // If models not found, force selection back to Parallel
          const parallelInput = this.querySelector('[data-leg-axis][value="Parallel"]');
          if (parallelInput) {
            parallelInput.checked = true;
            parallelInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    } catch (error) {
      console.error('Error selecting models by leg axis:', error);
      this.handleError(error);
    }
  }

  // The viewer column sticks below the sticky generator header, whose height
  // depends on which form (upload or customer data) is showing
  observeGeneratorHeaderHeight() {
    const header = this.querySelector(".generator__header");
    if (!header) return;
    const update = () =>
      this.style.setProperty("--generator-header-height", `${header.offsetHeight}px`);
    update();
    if ("ResizeObserver" in window) {
      new ResizeObserver(update).observe(header);
    }
  }

  toggleFormsVisibility(showUploadForm) {
    this.fileUploadForm.style.display = showUploadForm ? "flex" : "none";
    this.customerDataForm.style.display = showUploadForm ? "none" : "block";
  }

  getShapeConfig() {
    const checked = Array.from(this.shapeInputs).find((input) => input.checked);
    return (
      (checked && this.shapes.find((shape) => shape.key === checked.value)) ||
      this.defaultShape
    );
  }

  setShape(key) {
    const shape = this.shapes.find((candidate) => candidate.key === key) || this.defaultShape;
    if (key && shape?.key !== key) {
      console.warn(`Shape "${key}" is not available, falling back to ${shape?.key}`);
    }
    this.shapeInputs.forEach((input) => {
      input.checked = input.value === shape?.key;
    });
    this.applyShapeRules();
  }

  getSelectedThickness() {
    const checked = Array.from(this.thicknessInputs).find((input) => input.checked);
    return checked ? checked.value : "low";
  }

  setThickness(thickness) {
    this.thicknessInputs.forEach((input) => {
      input.checked = input.value === thickness;
    });
    this.applyShapeRules();
  }

  // Applies the selected shape's configuration to the panel. Selections that
  // are still valid for the shape are kept, the rest fall back to shape defaults.
  applyShapeRules() {
    const shape = this.getShapeConfig();
    if (!shape) return;

    this.applyThicknessRules(shape);
    this.applyCustomWidthRules(shape);
    this.applySizeRules(shape);
    this.applyLegLengthDiscrepancyRules(shape);
    this.applySizeChart(shape);

    if (this.shapeDescription) {
      this.shapeDescription.textContent = shape.description || "";
      this.shapeDescription.hidden = !shape.description;
    }

    this.updateAllProductData();
  }

  applyThicknessRules(shape) {
    const allowed = getAllowedThicknesses(shape, this.flatApproved);
    this.thicknessOptionElements.forEach((element) => {
      element.hidden = !allowed.includes(element.dataset.thicknessOption);
    });
    const thickness = resolveThickness(shape, this.getSelectedThickness(), this.flatApproved);
    this.thicknessInputs.forEach((input) => {
      input.checked = input.value === thickness;
    });
  }

  applyCustomWidthRules(shape) {
    this.customWidthOptionElements.forEach((element) => {
      element.hidden = !shape.allowCustomWidth;
    });
    // The per-side width steppers switch to Custom on edit, so hide them as well
    this.customWidthContainer.hidden = !shape.allowCustomWidth;
    if (!shape.allowCustomWidth && this.customWidthSwatch.checked) {
      this.setOptionInput("option2", this.lastWidthOption);
      this.customWidthInputsDirty = false;
    }
  }

  applySizeRules(shape) {
    if (this.rightSizeFieldset) {
      this.rightSizeFieldset.hidden = !shape.individualSizes;
    }
    if (this.leftSizeLegend) {
      this.leftSizeLegend.textContent = shape.individualSizes
        ? this.leftSizeLegend.dataset.sideLabel
        : this.leftSizeLegend.dataset.singleSizeLabel;
    }

    const sizeSelects = Array.from(this.optionInputs).filter((input) =>
      input.name.endsWith("-option1")
    );
    let sizeChanged = false;
    sizeSelects.forEach((select) => {
      const options = Array.from(select.options);
      const availableSizes = getAvailableSizes(
        shape,
        options.map((option) => option.value)
      );
      options.forEach((option) => {
        option.hidden = !availableSizes.includes(option.value);
      });
      const size = pickAvailableSize(select.value, availableSizes);
      if (size !== select.value) {
        select.value = size;
        sizeChanged = true;
      }
    });

    const leftSelect = sizeSelects.find((select) => select.name === "side:Left-option1");
    if (leftSelect && this.mirrorSizeToRight(leftSelect)) {
      sizeChanged = true;
    }

    // Only reset the custom width inputs when a size actually moved, so a
    // thickness or shape change does not wipe a partner's custom widths
    if (sizeChanged) {
      this.updateCustomWidthDefaults();
      this.customWidthInputsDirty = false;
    }
  }

  // Shapes without individual sizes use the left size for both feet.
  // Returns true when the right size was changed.
  mirrorSizeToRight(sizeInput) {
    if (sizeInput.name !== "side:Left-option1") return false;
    const shape = this.getShapeConfig();
    if (!shape || shape.individualSizes) return false;
    const rightSelect = Array.from(this.optionInputs).find(
      (input) => input.name === "side:Right-option1"
    );
    if (rightSelect && rightSelect.value !== sizeInput.value) {
      rightSelect.value = sizeInput.value;
      return true;
    }
    return false;
  }

  applyLegLengthDiscrepancyRules(shape) {
    const thickness = this.getSelectedThickness();
    const allowed = isLegLengthDiscrepancyAllowed(shape, thickness);
    const max = parseInt(shape.maxLegLengthDiscrepancy) || 6;

    if (this.lldContainer) {
      this.lldContainer.hidden = !shape.allowLegLengthDiscrepancy;
    }
    if (this.lldDisabledNotice) {
      this.lldDisabledNotice.hidden = allowed || !shape.allowLegLengthDiscrepancy;
    }

    this.legLengthInputs.forEach((input) => {
      input.max = max;
      input.disabled = !allowed;
      const buttons =
        input.closest("quantity-selector")?.querySelectorAll(".quantity-selector__button") || [];
      buttons.forEach((button) => {
        button.disabled = !allowed;
      });
      if (!allowed) {
        input.value = 0;
      } else if (parseInt(input.value) > max) {
        input.value = max;
      }
    });

    this.updateCompensationVisibility();
  }

  applySizeChart(shape) {
    if (!this.sizeChartTrigger) return;
    const hasFallback = !this.sizeChartTrigger.hasAttribute("data-no-fallback");
    const drawerId = shape.sizeChartDrawerId || (hasFallback ? "generator-size-chart" : null);
    this.sizeChartTrigger.hidden = !drawerId;
    if (drawerId) {
      this.sizeChartTrigger.setAttribute("aria-controls", drawerId);
    }
  }

  selectMaterial(materialValue, technicalValue) {
    // Update the dropdown display
    const selectedMaterialContainer = this.querySelector('[data-selected-material]');
    const materialData = this.materialsData[materialValue];
    
    if (selectedMaterialContainer && materialData) {
      selectedMaterialContainer.innerHTML = `
        <span class="material-swatch" style="background-image: url(${materialData.swatch}); width: 24px; height: 24px; border-radius: 3px; background-size: cover; flex-shrink: 0;"></span>
        <span class="material-name">${materialData.display_name}</span>
      `;
    }

    // Update the hidden radio input
    const radioInput = this.querySelector(`input[name="TOP_LAYER_MATERIAL"][value="${materialValue}"]`);
    if (radioInput) {
      // Uncheck all radio inputs first
      this.querySelectorAll('input[name="TOP_LAYER_MATERIAL"]').forEach(input => {
        input.checked = false;
      });
      
      // Check the selected one
      radioInput.checked = true;
      
      // Trigger change event for any existing logic
      radioInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Close the popover
    const popover = this.querySelector('#material-dropdown-popover');
    if (popover && popover.hide) {
      popover.hide();
    }
  }

  openPreviewGallery(materialName) {
    const materialData = this.materialsData[materialName];
    
    if (!materialData || !materialData.preview_images || materialData.preview_images.length === 0) {
      return;
    }

    // Update modal title with material display name
    const modalTitle = this.querySelector('#material-preview-title');
    if (modalTitle) {
      modalTitle.textContent = materialData.display_name;
    }

    // Clear previous images
    this.previewImagesContainer.innerHTML = '';

    // Add preview images
    materialData.preview_images.forEach((imageUrl, index) => {
      const imageElement = document.createElement('img');
      imageElement.src = imageUrl;
      imageElement.alt = `${materialName} preview ${index + 1}`;
      
      this.previewImagesContainer.appendChild(imageElement);
    });

    // Open the gallery drawer
    if (this.materialPreviewGallery && this.materialPreviewGallery.show) {
      this.materialPreviewGallery.show();
    }
  }
}

customElements.define("insole-generator", InsoleGenerator);

class ShipmentUpdatesCheckbox extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("change", this._onChanged);
  }

  _onChanged(event) {
    fetch(`${Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: { send_shipment_updates: event.target.checked ? "true" : "false" } }),
      keepalive: true, // Allows to make sure the request is fired even when submitting the form
    });
  }
}

customElements.define("shipment-updates-checkbox", ShipmentUpdatesCheckbox);
